import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  FormControl, FormControlLabel, RadioGroup, Radio, TextField, MenuItem, Alert,
  CircularProgress, Box, Divider,
} from '@mui/material';
import { blockedStockService } from '../services/api';

function toLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Діалог обсягу блокування після «Не пройшов».
 * Обсяг — один варіант за раз (радіокнопки).
 * props: open, onClose, onDone, orderQualityCheckId, qualityCheckId, orderId
 */
export default function BlockingScopeDialog({
  open,
  onClose,
  onDone,
  orderQualityCheckId,
  qualityCheckId,
  orderId,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [scope, setScope] = useState('since_last_pass');
  const [quantity, setQuantity] = useState('');
  const [fromAt, setFromAt] = useState('');
  const [boxLabel, setBoxLabel] = useState('');
  const [operatorId, setOperatorId] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (orderQualityCheckId) params.order_quality_check_id = orderQualityCheckId;
        if (qualityCheckId) params.quality_check_id = qualityCheckId;
        if (orderId) params.order_id = orderId;
        const res = await blockedStockService.previewFromQc(params);
        if (cancelled) return;
        const p = res.data;
        setPreview(p);
        const def =
          p.default_scope
          || (p.default_scopes?.length ? p.default_scopes[0] : null)
          || 'since_last_pass';
        setScope(def);
        setQuantity(String(p.suggested_quantity || 1));
        setFromAt(toLocalInputValue(p.suggested_from_at || p.last_pass_at));
        setReason(p.reason_hint || '');
        setBoxLabel('');
        setOperatorId(p.operators?.[0]?.id ? String(p.operators[0].id) : '');
        setShiftId(p.shifts?.[0]?.id ? String(p.shifts[0].id) : '');
        setPeriodStart(toLocalInputValue(p.suggested_from_at));
        setPeriodEnd(toLocalInputValue(new Date().toISOString()));
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Не вдалося завантажити превʼю блокування');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, orderQualityCheckId, qualityCheckId, orderId]);

  const submit = async () => {
    setError('');
    if (!scope) {
      setError('Оберіть варіант обсягу блокування');
      return;
    }
    const qty = parseInt(quantity, 10);
    if (!qty || qty < 1) {
      setError('Вкажіть кількість ≥ 1');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        order_quality_check_id: orderQualityCheckId || preview?.order_quality_check_id || undefined,
        quality_check_id: qualityCheckId || preview?.quality_check_id || undefined,
        order_id: orderId || preview?.order_id,
        scopes: [scope],
        quantity: qty,
        reason: reason.trim(),
      };
      if (scope === 'since_last_pass' || scope === 'from_time') {
        if (fromAt) payload.from_at = new Date(fromAt).toISOString();
      }
      if (scope === 'box') payload.box_label = boxLabel.trim();
      if (scope === 'operator') payload.operator_id = operatorId ? Number(operatorId) : undefined;
      if (scope === 'shift') payload.shift_id = shiftId ? Number(shiftId) : undefined;
      if (scope === 'machine_period') {
        if (periodStart) payload.period_start = new Date(periodStart).toISOString();
        if (periodEnd) payload.period_end = new Date(periodEnd).toISOString();
      }
      const res = await blockedStockService.fromQc(payload);
      onDone?.(res.data);
      onClose?.();
    } catch (e) {
      const d = e?.response?.data;
      let msg = 'Помилка блокування';
      if (typeof d === 'string') msg = d;
      else if (d?.detail) msg = d.detail;
      else if (d && typeof d === 'object') {
        msg = Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n');
      }
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Обсяг блокування продукції</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
        )}
        {!loading && preview && (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="body2">
              <b>{preview.product_name}</b>
              {preview.product_article ? ` · ${preview.product_article}` : ''}
              {preview.machine_identifier ? ` · ТПА ${preview.machine_identifier}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Виготовлено: {preview.order_produced} · уже заблоковано: {preview.already_blocked}
              {' · '}залишок артикулу (ГП): {preview.article_balance}
            </Typography>
            {preview.last_pass_at && (
              <Typography variant="caption" color="text.secondary">
                Остання успішна перевірка: {new Date(preview.last_pass_at).toLocaleString('uk-UA')}
              </Typography>
            )}
            <Alert severity="info" sx={{ py: 0.5 }}>
              За замовчуванням — усе з моменту останньої успішної перевірки. Оберіть один варіант обсягу.
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}

            <Divider>Обсяг (один варіант)</Divider>
            <FormControl component="fieldset">
              <RadioGroup
                value={scope}
                onChange={(e) => setScope(e.target.value)}
              >
                {(preview.scope_options || []).map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio size="small" />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {(scope === 'since_last_pass' || scope === 'from_time') && (
              <TextField
                label="З часу"
                type="datetime-local"
                size="small"
                value={fromAt}
                onChange={(e) => setFromAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            )}
            {scope === 'box' && (
              <TextField
                label="Ящик / мітка"
                size="small"
                value={boxLabel}
                onChange={(e) => setBoxLabel(e.target.value)}
                fullWidth
              />
            )}
            {scope === 'operator' && (
              <TextField
                select
                label="Оператор"
                size="small"
                value={operatorId}
                onChange={(e) => setOperatorId(e.target.value)}
                fullWidth
              >
                {(preview.operators || []).map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>{o.name}</MenuItem>
                ))}
              </TextField>
            )}
            {scope === 'shift' && (
              <TextField
                select
                label="Зміна"
                size="small"
                value={shiftId}
                onChange={(e) => setShiftId(e.target.value)}
                fullWidth
              >
                {(preview.shifts || []).map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                ))}
              </TextField>
            )}
            {scope === 'machine_period' && (
              <>
                <TextField
                  label="Період ТПА з"
                  type="datetime-local"
                  size="small"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Період ТПА до"
                  type="datetime-local"
                  size="small"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </>
            )}

            <TextField
              label="Кількість (шт)"
              type="number"
              size="small"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              inputProps={{ min: 1 }}
              helperText="Підказка з виробленого / залишку; можна змінити"
              fullWidth
            />
            <TextField
              label="Причина / коментар"
              size="small"
              multiline
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
            />
          </Box>
        )}
        {!loading && !preview && error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Скасувати</Button>
        <Button
          variant="contained"
          color="error"
          onClick={submit}
          disabled={saving || loading || !preview}
        >
          {saving ? <CircularProgress size={20} /> : 'Підтвердити блокування'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
